import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAuth, hasPermission } from "@/lib/auth";

interface ParseError {
  row: number;
  externalRef?: string;
  field?: string;
  message: string;
}

interface ParsedLine {
  itemSku: string;
  itemName: string;
  quantity: number;
  unit?: string;
  baseQuantity?: number;
  unitSellPrice?: number | null;
}

interface ParsedDraft {
  externalRef: string;
  orderName: string;
  fromLocationName: string;
  toLocationName: string;
  requestedDeliveryDate?: string | null;
  totalCost: number;
  items: ParsedLine[];
}

interface ParseResponse {
  upload: { id: string; status: string };
  parse: { rowCount: number; drafts: ParsedDraft[]; errors: ParseError[] };
}

function SignoffBadge({ status }: { status?: string }) {
  if (!status || status === "none") return null;
  const map: Record<string, { label: string; variant: any }> = {
    pending: { label: "Awaiting sign-off", variant: "secondary" },
    approved: { label: "Signed off", variant: "default" },
    rejected: { label: "Rejected", variant: "destructive" },
  };
  const cfg = map[status] || { label: status, variant: "outline" };
  return <Badge variant={cfg.variant} data-testid={`badge-signoff-${status}`}>{cfg.label}</Badge>;
}

export default function BulkOrderUpload() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user, roles } = useAuth();
  const canSignoff = hasPermission(user, "orders.approve" as any, roles);
  const fileRef = useRef<HTMLInputElement>(null);
  const [parseResult, setParseResult] = useState<ParseResponse | null>(null);
  const [history, setHistory] = useState(false);
  const [signoffNotes, setSignoffNotes] = useState<Record<string, string>>({});

  const { data: historyData } = useQuery<any[]>({
    queryKey: ["/api/orders/bulk-upload/history"],
    enabled: history,
  });

  const parseMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch("/api/orders/bulk-upload/parse", { method: "POST", body: fd, credentials: "include" });
      if (!r.ok) throw new Error((await r.json()).message || "Parse failed");
      return r.json() as Promise<ParseResponse>;
    },
    onSuccess: (data) => {
      setParseResult(data);
      queryClient.invalidateQueries({ queryKey: ["/api/orders/bulk-upload/history"] });
    },
    onError: (err: any) => toast({ variant: "destructive", title: "Parse failed", description: err.message }),
  });

  const processMutation = useMutation({
    mutationFn: async (mode: "draft" | "submit") => {
      if (!parseResult) throw new Error("No parse result");
      return apiRequest("POST", `/api/orders/bulk-upload/${parseResult.upload.id}/process`, { mode });
    },
    onSuccess: async (res: any) => {
      const data = await res.json();
      const skipped = parseResult?.parse.errors.length ?? 0;
      if (data.requiresSignoff) {
        toast({
          title: t("bulk_upload.submitted", "Submitted for sign-off"),
          description: `${data.pending} order(s) are waiting for an authorized user to sign off${skipped ? `; ${skipped} row(s) skipped` : ""}.`,
        });
      } else {
        toast({
          title: t("bulk_upload.processed", "Bulk upload processed"),
          description: `Created ${data.created}, skipped ${data.failed.length + skipped}`,
        });
      }
      setParseResult(null);
      queryClient.invalidateQueries({ queryKey: ["/api/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/orders/bulk-upload/history"] });
    },
    onError: (err: any) => toast({ variant: "destructive", title: "Process failed", description: err.message }),
  });

  const signoffMutation = useMutation({
    mutationFn: async ({ uploadId, decision, note }: { uploadId: string; decision: "approve" | "reject"; note?: string }) => {
      return apiRequest("POST", `/api/orders/bulk-upload/${uploadId}/signoff`, { decision, note });
    },
    onSuccess: async (res: any) => {
      const data = await res.json();
      if (data.decision === "approve") {
        toast({ title: t("bulk_upload.approved", "Batch approved"), description: `Created ${data.created}, skipped ${data.failed.length}` });
      } else {
        toast({ title: t("bulk_upload.rejected", "Batch rejected"), description: t("bulk_upload.rejectedDesc", "No orders were created.") });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/orders/bulk-upload/history"] });
    },
    onError: (err: any) => toast({ variant: "destructive", title: "Sign-off failed", description: err.message }),
  });

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) parseMutation.mutate(f);
  };

  const pendingSignoffs = (historyData || []).filter((h) => h.signoffStatus === "pending");

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="page-bulk-order-upload">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("bulk_upload.title", "Bulk Order Upload")}</h1>
          <p className="text-sm text-muted-foreground">{t("bulk_upload.subtitle", "Upload XLSX or CSV files to create many orders at once.")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open("/api/orders/bulk-upload/template", "_blank")} data-testid="button-download-template">
            {t("bulk_upload.template", "Download template")}
          </Button>
          <Button variant="outline" onClick={() => setHistory((v) => !v)} data-testid="button-toggle-history">
            {history ? t("bulk_upload.hideHistory", "Hide history") : t("bulk_upload.showHistory", "Show history")}
          </Button>
        </div>
      </div>

      {!parseResult && (
        <Card>
          <CardHeader>
            <CardTitle>{t("bulk_upload.upload", "Upload a file")}</CardTitle>
            <CardDescription>
              {t("bulk_upload.uploadDesc", "Rows with the same external_ref roll up into a single order. Columns: external_ref, from_location, to_location, item_sku, quantity, unit_of_measure, unit_sell_price, requested_delivery_date (required), notes, order_name. Each item's unit of measure must be valid for that item, and quantities can't exceed available stock at the source. Valid rows are imported; invalid rows are skipped and listed.")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={onFileChange} data-testid="input-bulk-file" />
            {parseMutation.isPending && <p className="mt-3 text-sm">{t("bulk_upload.parsing", "Parsing…")}</p>}
          </CardContent>
        </Card>
      )}

      {/* Sign-off queue: batches awaiting approval. */}
      {pendingSignoffs.length > 0 && (
        <Card data-testid="card-signoff-queue">
          <CardHeader>
            <CardTitle>{t("bulk_upload.signoffTitle", "Awaiting sign-off")}</CardTitle>
            <CardDescription>
              {canSignoff
                ? t("bulk_upload.signoffDescApprover", "These bulk uploads are held until you sign off. Approving creates the orders; rejecting discards them.")
                : t("bulk_upload.signoffDescViewer", "These bulk uploads are waiting for an authorized user to sign off before the orders are created.")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {pendingSignoffs.map((h) => (
              <div key={h.id} className="border rounded-md p-4 space-y-3" data-testid={`signoff-item-${h.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{h.filename}</div>
                    <div className="text-xs text-muted-foreground">
                      {t("bulk_upload.ordersWaiting", "{{n}} order(s) waiting", { n: h.parsedOrderCount })} · {new Date(h.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <SignoffBadge status={h.signoffStatus} />
                </div>
                {canSignoff && (
                  <div className="space-y-2">
                    <Textarea
                      placeholder={t("bulk_upload.signoffNote", "Optional note") as string}
                      value={signoffNotes[h.id] || ""}
                      onChange={(e) => setSignoffNotes((s) => ({ ...s, [h.id]: e.target.value }))}
                      data-testid={`input-signoff-note-${h.id}`}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        disabled={signoffMutation.isPending}
                        onClick={() => signoffMutation.mutate({ uploadId: h.id, decision: "reject", note: signoffNotes[h.id] })}
                        data-testid={`button-reject-${h.id}`}
                      >
                        {t("bulk_upload.reject", "Reject")}
                      </Button>
                      <Button
                        disabled={signoffMutation.isPending}
                        onClick={() => signoffMutation.mutate({ uploadId: h.id, decision: "approve", note: signoffNotes[h.id] })}
                        data-testid={`button-approve-${h.id}`}
                      >
                        {t("bulk_upload.approve", "Approve & create orders")}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {parseResult && parseResult.parse.errors.length > 0 && (
        <Alert variant="destructive">
          <AlertTitle>{t("bulk_upload.errorsTitle", "{{n}} rows skipped", { n: parseResult.parse.errors.length })}</AlertTitle>
          <AlertDescription>
            <ul className="list-disc ps-5 mt-2 text-sm max-h-48 overflow-auto">
              {parseResult.parse.errors.map((e, i) => (
                <li key={i}>Row {e.row}{e.externalRef ? ` (${e.externalRef})` : ""}{e.field ? ` [${e.field}]` : ""}: {e.message}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {parseResult && parseResult.parse.drafts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t("bulk_upload.reviewTitle", "Review parsed orders")}</CardTitle>
            <CardDescription>
              {t("bulk_upload.reviewDesc", "{{rows}} rows parsed → {{orders}} orders ready.", {
                rows: parseResult.parse.rowCount,
                orders: parseResult.parse.drafts.length,
              })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>external_ref</TableHead>
                  <TableHead>from → to</TableHead>
                  <TableHead>{t("bulk_upload.delivery", "Delivery")}</TableHead>
                  <TableHead>{t("bulk_upload.lines", "Lines")}</TableHead>
                  <TableHead className="text-end">{t("bulk_upload.total", "Total")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parseResult.parse.drafts.map((d) => (
                  <TableRow key={d.externalRef} data-testid={`row-draft-${d.externalRef}`}>
                    <TableCell className="font-mono text-xs align-top">{d.externalRef}</TableCell>
                    <TableCell className="align-top">{d.fromLocationName} → {d.toLocationName}</TableCell>
                    <TableCell className="align-top text-xs">
                      {d.requestedDeliveryDate ? new Date(d.requestedDeliveryDate).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="align-top">
                      <ul className="text-xs space-y-0.5">
                        {d.items.map((it, i) => (
                          <li key={i} data-testid={`line-${d.externalRef}-${i}`}>
                            {it.itemSku} — {it.quantity} {it.unit || ""}
                          </li>
                        ))}
                      </ul>
                    </TableCell>
                    <TableCell className="text-end align-top">{d.totalCost.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setParseResult(null)} data-testid="button-discard">{t("common.cancel")}</Button>
              <Button variant="secondary" onClick={() => processMutation.mutate("draft")} disabled={processMutation.isPending} data-testid="button-create-drafts">
                {t("bulk_upload.createDrafts", "Create as drafts")}
              </Button>
              <Button onClick={() => processMutation.mutate("submit")} disabled={processMutation.isPending} data-testid="button-submit-orders">
                {t("bulk_upload.submitOrders", "Submit for sign-off")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {history && (
        <Card>
          <CardHeader><CardTitle>{t("bulk_upload.historyTitle", "Upload history")}</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("bulk_upload.when", "When")}</TableHead>
                  <TableHead>{t("bulk_upload.file", "File")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                  <TableHead>{t("bulk_upload.signoff", "Sign-off")}</TableHead>
                  <TableHead className="text-end">{t("bulk_upload.rows", "Rows")}</TableHead>
                  <TableHead className="text-end">{t("bulk_upload.orders", "Orders")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(historyData || []).map((h) => (
                  <TableRow key={h.id} data-testid={`row-history-${h.id}`}>
                    <TableCell className="text-xs">{new Date(h.createdAt).toLocaleString()}</TableCell>
                    <TableCell>{h.filename}</TableCell>
                    <TableCell><Badge>{h.status}</Badge></TableCell>
                    <TableCell>
                      <SignoffBadge status={h.signoffStatus} />
                      {(h.signoffStatus === "approved" || h.signoffStatus === "rejected") && h.signoffAt && (
                        <div className="text-[10px] text-muted-foreground mt-0.5" data-testid={`text-signoff-meta-${h.id}`}>
                          {h.signoffByName ? `${h.signoffByName} · ` : ""}{new Date(h.signoffAt).toLocaleString()}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-end">{h.rowCount}</TableCell>
                    <TableCell className="text-end">{h.processedDraftCount + h.processedSubmitCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
